import { useState } from 'react'
import { api, ApiError, requestBlob } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { money, percent } from '@/lib/format'
import { combine, useAsync } from '@/lib/useAsync'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Loading, cx } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, MoneyInput, Select } from '@/components/ui/form'
import { Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { AttainmentRow, BreakdownRow, Paged, SalesTargetItem, TargetPeriodType, TrendPoint, UserListItem } from '@/types/api'

type Tab = 'trend' | 'breakdown' | 'targets'

export function Analytics() {
  const { can, canSeeAny } = useAuth()
  const { current } = useCountry()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>('trend')

  async function exportCsv() {
    try {
      const blob = await requestBlob('/analytics/export', {
        query: { countryId: current?.countryId },
      })

      const href = URL.createObjectURL(blob)
      const anchor = document.createElement('a')

      anchor.href = href
      anchor.download = `quotations-${current?.isoCode ?? 'all'}.csv`
      anchor.click()

      URL.revokeObjectURL(href)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not build the export', 'error')
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Performance"
        hint="Money is net of tax. A quotation counts toward pipeline when it was raised and toward won or lost when it closed."
        actions={
          can('analytics.export') && <Button onClick={() => void exportCsv()}>Export CSV</Button>
        }
      />

      <Tabs
        tabs={[
          { id: 'trend', label: 'Trend' },
          { id: 'breakdown', label: 'Breakdown' },
          ...(canSeeAny('salestarget.view') ? [{ id: 'targets' as const, label: 'Targets' }] : []),
        ]}
        active={tab}
        onSelect={setTab}
      />

      {tab === 'trend' && <Trend />}
      {tab === 'breakdown' && <Breakdown />}
      {tab === 'targets' && <Targets />}
    </>
  )
}

function Trend() {
  const { current } = useCountry()
  const [months, setMonths] = useState(12)

  const state = useAsync<TrendPoint[]>(
    (signal) => api.get<TrendPoint[]>('/analytics/trend', { countryId: current?.countryId, months }, signal),
    [current?.countryId, months],
  )

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (state.loading && !state.data) return <Loading />
  if (state.error) return <ErrorState title={state.error.title} detail={state.error.detail} onRetry={state.reload} />
  if (!state.data || state.data.length === 0) {
    return (
      <Card>
        <EmptyState title="No history yet" hint="A trend needs quotations raised and closed over time." />
      </Card>
    )
  }

  // One chart per currency: ringgit and Singapore dollars share no axis.
  const currencies = [...new Set(state.data.map((point) => point.currencyCode))]

  return (
    <>
      <div className="mb-4 w-44">
        <Select value={months} onChange={(event) => setMonths(Number(event.target.value))} aria-label="Months">
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
          <option value={24}>Last 24 months</option>
        </Select>
      </div>

      {currencies.map((currency) => {
        const points = state.data!.filter((point) => point.currencyCode === currency)
        const peak = Math.max(...points.map((point) => Math.max(point.issuedValue, point.wonValue)), 1)
        const symbol = points[0]?.currencySymbol ?? currency

        return (
          <Card key={currency} className="mb-5">
            <CardHeader
              title={`Raised and won · ${currency}`}
              hint="Each pair of bars is one month, net of tax."
              action={
                <div className="flex items-center gap-4">
                  <LegendKey className="bg-accent-wash" label="Raised" />
                  <LegendKey className="bg-accent" label="Won" />
                </div>
              }
            />

            <div className="overflow-x-auto px-5 py-5">
              {/* A fixed height on the plot area, not on the row: a percentage height
                  needs a definite parent, and h-full inside an items-end flex row
                  resolves against nothing at all. */}
              <div className="flex min-w-[36rem] gap-3">
                {points.map((point) => (
                  <div key={point.label} className="flex flex-1 flex-col items-center gap-2">
                    <div className="flex h-40 w-full items-end justify-center gap-1">
                      <Bar
                        height={(point.issuedValue / peak) * 100}
                        className="bg-accent-wash"
                        title={`${point.label}: ${money(point.issuedValue, symbol, 0)} raised`}
                      />
                      <Bar
                        height={(point.wonValue / peak) * 100}
                        className="bg-accent"
                        title={`${point.label}: ${money(point.wonValue, symbol, 0)} won`}
                      />
                    </div>
                    <span className="font-mono text-[10px] text-muted">{point.label.slice(0, 3)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Whole figures with separators rather than "RM 38k". A table is read for
                the number, and a rounded one cannot be reconciled against anything -
                two months both reading 38k are not the same 38k. The compact form
                stays where space genuinely forces it, on the dashboard tiles. */}
            <Table>
              <thead>
                <tr>
                  <Th>Month</Th>
                  <Th numeric>Raised</Th>
                  <Th numeric>Won</Th>
                  <Th numeric>Lost</Th>
                </tr>
              </thead>
              <tbody>
                {points.map((point) => (
                  <tr key={point.label}>
                    <Td>{point.label}</Td>
                    <Td numeric>
                      {point.issuedCount} · {money(point.issuedValue, symbol, 0)}
                    </Td>
                    <Td numeric>
                      {point.wonCount} · {money(point.wonValue, symbol, 0)}
                    </Td>
                    <Td numeric className="text-muted">
                      {point.lostCount} · {money(point.lostValue, symbol, 0)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )
      })}
    </>
  )
}

function Bar({ height, className, title }: { height: number; className: string; title: string }) {
  return (
    <div
      title={title}
      className={cx('w-3 rounded-t-sm', className)}
      // A zero-value month still shows a hairline, so an empty month reads as empty
      // rather than as a missing bar.
      style={{ height: `${Math.max(height, 1)}%` }}
    />
  )
}

/**
 * Two colours carrying two figures need naming somewhere other than a tooltip, which
 * only exists once somebody has already guessed which bar to point at.
 */
function LegendKey({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-[12px] text-muted">
      <span className={cx('h-2.5 w-2.5 rounded-sm', className)} aria-hidden="true" />
      {label}
    </span>
  )
}

function Breakdown() {
  const { current } = useCountry()
  const [by, setBy] = useState<'Country' | 'Rep' | 'Product'>('Rep')

  const state = useAsync<BreakdownRow[]>(
    (signal) =>
      api.get<BreakdownRow[]>(
        '/analytics/breakdown',
        { by, countryId: by === 'Country' ? undefined : current?.countryId },
        signal,
      ),
    [by, current?.countryId],
  )

  return (
    <>
      <div className="mb-4 w-48">
        <Select
          value={by}
          onChange={(event) => setBy(event.target.value as 'Country' | 'Rep' | 'Product')}
          aria-label="Break down by"
        >
          <option value="Rep">By rep</option>
          <option value="Product">By product</option>
          <option value="Country">By country</option>
        </Select>
      </div>

      {state.loading && <Loading />}
      {state.error && <ErrorState title={state.error.title} detail={state.error.detail} onRetry={state.reload} />}

      {state.data && (
        <Card>
          {state.data.length === 0 ? (
            <EmptyState title="Nothing to break down yet" />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>{by}</Th>
                  <Th numeric>Open pipeline</Th>
                  <Th numeric>Won</Th>
                  <Th numeric>Win rate</Th>
                </tr>
              </thead>
              <tbody>
                {state.data.map((row) => (
                  <tr key={`${row.key}-${row.currencyCode}`}>
                    <Td>
                      <span className="font-medium">{row.label}</span>
                      <span className="ml-2 font-mono text-[11px] text-faint">{row.currencyCode}</span>
                    </Td>
                    <Td numeric>
                      {row.pipelineCount} · {money(row.pipelineValue, row.currencySymbol, 0)}
                    </Td>
                    <Td numeric>
                      {row.wonCount} · {money(row.wonValue, row.currencySymbol, 0)}
                    </Td>
                    <Td numeric>{percent(row.winRateByCount)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </>
  )
}

function Targets() {
  const { can } = useAuth()
  const { current } = useCountry()
  const toast = useToast()
  const [year] = useState(new Date().getFullYear())
  const [periodType, setPeriodType] = useState<TargetPeriodType>('Quarter')
  const [setting, setSetting] = useState(false)
  const [editing, setEditing] = useState<AttainmentRow | null>(null)
  const [removing, setRemoving] = useState<AttainmentRow | null>(null)
  const [deleting, setDeleting] = useState(false)
  const manageable = can('salestarget.set')

  const attainment = useAsync<AttainmentRow[]>(
    (signal) =>
      api.get<AttainmentRow[]>(
        '/analytics/attainment',
        { countryId: current?.countryId, periodType, year },
        signal,
      ),
    [current?.countryId, periodType, year],
  )

  async function remove(row: AttainmentRow) {
    setDeleting(true)
    try {
      await api.del(`/targets/${row.targetId}`)
      toast.show(`${row.userName}'s ${row.periodLabel} target removed`, 'success')
      setRemoving(null)
      attainment.reload()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not remove that target',
        'error',
      )
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-44">
          <Select
            value={periodType}
            onChange={(event) => setPeriodType(event.target.value as TargetPeriodType)}
            aria-label="Period"
          >
            <option value="Month">Monthly</option>
            <option value="Quarter">Quarterly</option>
            <option value="Year">Yearly</option>
          </Select>
        </div>

        {manageable && (
          <Button variant="primary" onClick={() => setSetting(true)}>
            Set a target
          </Button>
        )}
      </div>

      {attainment.loading && <Loading />}
      {attainment.error && (
        <ErrorState title={attainment.error.title} detail={attainment.error.detail} onRetry={attainment.reload} />
      )}

      {attainment.data && (
        <Card>
          {attainment.data.length === 0 ? (
            <EmptyState
              title={`No ${periodType.toLowerCase()} targets for ${year}`}
              hint="Attainment is what was won inside the target's own period, so a target has to exist first."
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Rep</Th>
                  <Th>Period</Th>
                  <Th numeric>Target</Th>
                  <Th numeric>Won</Th>
                  <Th>Attainment</Th>
                  {manageable && <Th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {attainment.data.map((row) => (
                  <tr key={`${row.userId}-${row.periodStart}`}>
                    <Td>
                      <span className="font-medium">{row.userName}</span>
                      <span className="ml-2 font-mono text-[11px] text-faint">{row.countryIsoCode}</span>
                    </Td>
                    <Td className="text-muted">{row.periodLabel}</Td>
                    <Td numeric>{money(row.targetAmount, row.currencySymbol, 0)}</Td>
                    <Td numeric>{money(row.wonValue, row.currencySymbol, 0)}</Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-sunken">
                          <div
                            className={cx(
                              'h-full rounded-full',
                              row.attainmentPercent >= 100 ? 'bg-pos' : 'brand-gradient',
                            )}
                            style={{ width: `${Math.min(row.attainmentPercent, 100)}%` }}
                          />
                        </div>
                        <span className="tabular text-[13px]">{percent(row.attainmentPercent, 0)}</span>
                        {row.attainmentPercent >= 100 && <Badge tone="positive">Made it</Badge>}
                      </div>
                    </Td>
                    {manageable && (
                      <Td>
                        <span className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => setEditing(row)}>
                            Edit
                          </Button>
                          <Button size="sm" onClick={() => setRemoving(row)}>
                            Delete
                          </Button>
                        </span>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* Keyed so opening a second row's target draws that row's figures rather than
          the previous one's - the fields are seeded from the row, and seeding only
          happens on mount. */}
      <SetTarget
        key={editing?.targetId ?? 'new'}
        open={setting || editing !== null}
        editing={editing}
        year={year}
        periodType={periodType}
        onClose={() => {
          setSetting(false)
          setEditing(null)
        }}
        onSaved={() => {
          setSetting(false)
          setEditing(null)
          attainment.reload()
        }}
      />

      <Dialog
        open={removing !== null}
        title={removing ? `Remove ${removing.userName}'s ${removing.periodLabel} target?` : 'Remove this target?'}
        onClose={() => setRemoving(null)}
        footer={
          <>
            <Button size="sm" onClick={() => setRemoving(null)}>
              Keep it
            </Button>
            <Button
              size="sm"
              variant="danger"
              loading={deleting}
              onClick={() => removing && void remove(removing)}
            >
              Remove
            </Button>
          </>
        }
      >
        {/* A target is a number somebody typed, not a record of anything that happened,
            so removing it destroys no history - the quotations it was measured against
            are untouched, and the row simply stops being reported. */}
        <p className="text-[13px] text-ink-2">
          The quotations behind the {money(removing?.wonValue ?? 0, removing?.currencySymbol ?? '', 0)} won
          are untouched; only the number being measured against goes. Set it again if it turns
          out to be needed.
        </p>
      </Dialog>
    </>
  )
}

/**
 * Sets a target, and edits one.
 *
 * <p>
 * The same dialog for both because the API is the same call: a target is keyed by rep,
 * country, period type and period, and setting one again replaces it. Which is exactly
 * why editing locks the rep and the period - changing either would not revise this
 * target, it would write a second one beside it and leave the first standing.
 * </p>
 */
function SetTarget({
  open,
  editing,
  year,
  periodType,
  onClose,
  onSaved,
}: {
  open: boolean
  editing: AttainmentRow | null
  year: number
  periodType: TargetPeriodType
  onClose: () => void
  onSaved: () => void
}) {
  const { current } = useCountry()
  const toast = useToast()
  const [userId, setUserId] = useState(editing?.userId ?? '')
  const [periodIndex, setPeriodIndex] = useState(editing?.periodIndex ?? 1)
  const [amount, setAmount] = useState(editing ? String(editing.targetAmount) : '')
  const [saving, setSaving] = useState(false)

  // A picker, not a listing: it wants every name at once. Capped at the API's own page
  // limit, which is far above any Codemax headcount - if that ever stops being true this
  // becomes a search box rather than a longer list.
  const users = useAsync<UserListItem[]>(
    (signal) =>
      open
        ? api.get<Paged<UserListItem>>('/users', { pageSize: 200 }, signal).then((paged) => paged.items)
        : Promise.resolve([]),
    [open],
  )

  const existing = useAsync<SalesTargetItem[]>(
    (signal) =>
      open
        ? api.get<SalesTargetItem[]>(
            '/targets',
            { countryId: current?.countryId, periodType, year },
            signal,
          )
        : Promise.resolve([]),
    [open, current?.countryId, periodType, year],
  )

  // The rep list and the targets already set both decide what this form should say, so it
  // is drawn once, complete, rather than filling in around the person choosing a name.
  const gate = combine(users, existing)

  const periods =
    periodType === 'Month'
      ? Array.from({ length: 12 }, (_, index) => ({ value: index + 1, label: MONTHS[index]! }))
      : periodType === 'Quarter'
        ? [1, 2, 3, 4].map((quarter) => ({ value: quarter, label: `Q${quarter}` }))
        : [{ value: 1, label: String(year) }]

  async function save() {
    // An edit belongs to the country its own row came from, which is not necessarily the
    // one selected: with no country picked the listing spans all of them, and taking the
    // country from the picker would move the target rather than revise it.
    const countryId = editing?.countryId ?? current?.countryId
    if (!countryId || !userId) return

    setSaving(true)
    try {
      await api.put('/targets', {
        userId,
        countryId,
        periodType,
        year,
        periodIndex,
        targetAmount: Number(amount),
      })

      toast.show(editing ? 'Target updated' : 'Target set', 'success')
      onSaved()
      if (!editing) setAmount('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not set the target', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title={editing ? 'Edit target' : 'Set a target'}
      description={
        editing
          ? `${editing.userName}, ${editing.periodLabel}, in ${editing.currencyCode}, net of tax. The rep and the period are what identify this target, so only the figure changes here.`
          : `For ${current?.name ?? 'this country'}, in ${current?.currencyCode ?? ''}, net of tax. Setting one again replaces it rather than adding a second.`
      }
      onClose={onClose}
      loading={gate.loading}
      error={gate.error}
      onRetry={gate.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!userId || !amount} onClick={() => void save()}>
            {editing ? 'Save target' : 'Set target'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Rep" required>
          {(id) => (
            <Select
              id={id}
              value={userId}
              disabled={editing !== null}
              onChange={(event) => setUserId(event.target.value)}
            >
              <option value="">Choose a person</option>
              {users.data
                ?.filter((user) => user.isActive)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
            </Select>
          )}
        </Field>

        <Field label="Period" hint="The dates are worked out from this, so every target for a period covers the same days.">
          {(id) => (
            <Select
              id={id}
              value={periodIndex}
              disabled={editing !== null}
              onChange={(event) => setPeriodIndex(Number(event.target.value))}
            >
              {periods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label} {year}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label={`Target (${current?.currencyCode ?? ''})`} required>
          {(id) => <MoneyInput id={id} value={amount} onChange={(event) => setAmount(event.target.value)} />}
        </Field>

        <Field label="Year">{(id) => <Input id={id} value={year} disabled />}</Field>

        {!editing && existing.data && existing.data.length > 0 && (
          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
              Already set for {year}
            </p>
            <ul className="flex flex-col gap-1 text-[13px] text-muted">
              {existing.data.map((target) => (
                <li key={target.id} className="flex justify-between gap-4">
                  <span>
                    {target.userName} · {target.periodLabel}
                  </span>
                  <span className="tabular">{money(target.targetAmount, target.currencyCode, 0)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  )
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
